<template>
  <div class="flex flex-col">
    <!-- Node -->
    <div
      v-if="level > 0"
      class="flex items-center gap-2"
      :class="[
        !isFolder && hasVisualizer(element.file_type)
          ? 'cursor-pointer hover:bg-gray-50 rounded-lg px-1 -mx-1'
          : 'cursor-default',
      ]"
      :style="{ marginLeft: `${level * 1.25}rem` }"
      @click="!isFolder && hasVisualizer(element.file_type) && handleFileOpen()"
    >
      <!-- Toggle arrows -->
      <img
        v-if="isFolder"
        class="h-4 cursor-pointer hover:opacity-80 transition-opacity"
        :src="accordion_open"
        @click.stop="toggleVisible"
        v-show="folderVisible"
        title="Collapse folder"
      />
      <img
        v-if="isFolder"
        class="h-4 cursor-pointer hover:opacity-80 transition-opacity"
        :src="accordion_close"
        @click.stop="toggleVisible"
        v-show="!folderVisible"
        title="Expand folder"
      />

      <!-- File / Folder Icon: Iconify when available, local SVG otherwise -->
      <span class="h-5 w-5 shrink-0 select-none" :title="element.file_type">
        <Icon v-if="iconifyAvailable" :icon="iconifyName" class="h-5 w-5" />
        <img
          v-else-if="iconifyAvailable === false"
          :src="iconName"
          class="h-5 w-5"
          @error="localIconMissing = true"
        />
      </span>

      <!-- Name -->
      <p class="truncate text-sm">{{ element.name }}</p>

      <!-- Info (e.g., dimension) -->
      <p v-if="element?.info?.dim" class="ml-auto text-xs text-gray-500">
        {{ element.info.dim }}
      </p>
    </div>

    <!-- Children -->
    <transition name="fade" mode="out-in">
      <div v-if="element.children" v-show="folderVisible" class="flex flex-col">
        <TreeElement
          v-for="(child, idx) in element.children"
          :key="idx"
          :element="child"
          :level="level + 1"
          :expandAll="expandAll"
          :parentPath="currentPath"
          :onFileOpen="onFileOpen"
        />
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, defineProps } from "vue";
import { Icon, iconLoaded, loadIcon } from "@iconify/vue";

const props = defineProps({
  element: { type: Object, required: true },
  level: { type: Number, required: true },
  expandAll: { type: Boolean, required: true },
  parentPath: { type: String, default: "" },
  onFileOpen: { type: Function, default: null },
});

const emit = defineEmits(["file-open"]);

const folderVisible = ref(true);
const accordion_open = ref(null);
const accordion_close = ref(null);

onMounted(() => {
  const base = import.meta?.env?.BASE_URL || process.env.BASE_URL || "/";
  accordion_open.value = `${base}svg/accordion_open.svg`;
  accordion_close.value = `${base}svg/accordion_close.svg`;
  folderVisible.value = props.expandAll;
});

watch(
  () => props.expandAll,
  (val) => {
    if (props.level !== 0) folderVisible.value = val;
  },
);

const isFolder = computed(() =>
  props.element && Array.isArray(props.element.children)
    ? props.element.children.length > 0
    : false,
);

const currentPath = computed(() =>
  props.parentPath
    ? props.parentPath + "/" + props.element.name
    : props.element.name,
);

const toggleVisible = () => {
  folderVisible.value = !folderVisible.value;
};

const FILE_TYPE_TO_VISUALIZER = {
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  webp: "image",
  bmp: "image",
  svg: "image",
  ico: "image",
  avif: "image",
  heic: "image",
  heif: "image",
  pdf: "pdf",
  py: "code",
  js: "code",
  mjs: "code",
  ts: "code",
  tsx: "code",
  jsx: "code",
  vue: "code",
  html: "code",
  htm: "code",
  css: "code",
  scss: "code",
  less: "code",
  json: "code",
  yaml: "code",
  yml: "code",
  xml: "code",
  md: "code",
  txt: "code",
  csv: "code",
  sh: "code",
  bash: "code",
  r: "code",
  jl: "code",
  cpp: "code",
  cxx: "code",
  c: "code",
  h: "code",
  java: "code",
  rs: "code",
  go: "code",
  rb: "code",
  php: "code",
  toml: "code",
  ini: "code",
  cfg: "code",
  conf: "code",
  env: "code",
  sql: "code",
  tex: "code",
  matlab: "code",
  f90: "code",
  f95: "code",
};

function hasVisualizer(fileType) {
  return !!FILE_TYPE_TO_VISUALIZER[(fileType || "").toLowerCase()];
}

function handleFileOpen() {
  const payload = {
    name: props.element.name,
    file_type: props.element.file_type,
    path: currentPath.value,
  };
  props.onFileOpen?.(payload);
  emit("file-open", payload);
}

// file_type -> vscode-icons "file-type-*" name, where the two differ.
// Types not listed here are tried as-is (e.g. "json" -> file-type-json).
const VSCODE_ICON_ALIASES = {
  py: "python",
  ipynb: "jupyter",
  ts: "typescript",
  tsx: "typescript",
  jsx: "js",
  mjs: "js",
  md: "markdown",
  txt: "text",
  yml: "yaml",
  sh: "shell",
  bash: "shell",
  htm: "html",
  pdf: "pdf2",
  jl: "julia",
  m: "matlab",
  f90: "fortran",
  f95: "fortran",
  h: "cheader",
  hpp: "cppheader",
  cxx: "cpp",
  rs: "rust",
  rb: "ruby",
  cfg: "config",
  conf: "config",
  xls: "excel",
  xlsx: "excel",
  doc: "word",
  docx: "word",
  ppt: "powerpoint",
  pptx: "powerpoint",
  gz: "zip",
  tar: "zip",
  "7z": "zip",
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  webp: "image",
  bmp: "image",
  tif: "image",
  tiff: "image",
  mp4: "video",
  avi: "video",
  mov: "video",
  mkv: "video",
  mp3: "audio",
  wav: "audio",
  flac: "audio",
  bin: "binary",
};

const iconifyName = computed(() => {
  const type = (props.element.file_type || "").toLowerCase();
  if (type === "folder") {
    return folderVisible.value
      ? "vscode-icons:default-folder-opened"
      : "vscode-icons:default-folder";
  }
  if (type === "h5-folder") return "vscode-icons:folder-type-db";
  return `vscode-icons:file-type-${VSCODE_ICON_ALIASES[type] ?? type}`;
});

// null = still checking, true = use Iconify, false = use the local SVG
const iconifyAvailable = ref(null);

watch(
  iconifyName,
  async (name) => {
    if (iconLoaded(name)) {
      iconifyAvailable.value = true;
      return;
    }
    iconifyAvailable.value = null;
    let available = false;
    try {
      available = !!(await loadIcon(name));
    } catch {
      available = false;
    }
    // Ignore results for an icon name that changed while loading
    if (iconifyName.value === name) iconifyAvailable.value = available;
  },
  { immediate: true },
);

// True when the local SVG for this file type does not exist (unknown extension)
const localIconMissing = ref(false);
watch(
  () => props.element.file_type,
  () => {
    localIconMissing.value = false;
  },
);

/**
 * Fallback local SVG, used when Iconify has no icon for the file type.
 * Uses different icon folders for "extra" and "high-contrast".
 * Unknown extensions get the generic default file icon.
 */
const iconName = computed(() => {
  const base = import.meta?.env?.BASE_URL || process.env.BASE_URL || "/";
  const type = props.element.file_type;

  if (!type || localIconMissing.value) {
    return `${base}svg/icons/high-contrast/default.svg`;
  } else if (type === "folder") {
    return `${base}svg/icons/extra/folder.svg`;
  } else if (type === "h5-folder") {
    // Keep existing special h5 folder logic
    return `${base}svg/icons/extra/h5-folder.svg`;
  } else {
    return `${base}svg/icons/high-contrast/${props.element.file_type}.svg`;
  }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
